import React, {useState, useEffect, useRef, useCallback} from 'react';

import {IconChevronLeft, IconChevronRight} from '@tabler/icons-react';
import Image from 'next/image';
import styled from 'styled-components';

import {dimensions} from '@stryd/react-ui';

import {useScrollCarousel} from './useScrollCarousel';

const columnLayoutBreakpoint = dimensions.breakpoints.tabletLandscape;

interface MediaItem {
  id: string;
  src: string;
  alt: string;
  type: 'image' | 'video';
  thumbnail?: string;
}

interface ProductImageCarouselProps {
  mediaItems: MediaItem[];
  selectedProduct?: string;
  className?: string;
  enableScrollAdvancement?: boolean;
}

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #f8f9fa;

  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    height: 450px;
  }

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    width: 100%;
    height: 100%;
    position: sticky;
    top: 0;
  }
`;

const MainMediaContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(
    circle,
    rgb(254 243 199) 0%,
    rgb(255 237 213) 50%,
    rgb(254 215 170) 100%
  );
`;

const MediaWrapper = styled.div<{isVideo?: boolean}>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.isVideo &&
    `
    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `}
`;

const StyledImage = styled(Image)`
  max-width: 75%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const NavigationButton = styled.button<{position: 'left' | 'right'}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.position === 'left' ? 'left: 20px;' : 'right: 20px;')}
  z-index: 10;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-50%) scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media screen and (max-width: ${columnLayoutBreakpoint}) {
    width: 40px;
    height: 40px;
    ${(props) => (props.position === 'left' ? 'left: 12px;' : 'right: 12px;')}
  }
`;

const ThumbnailContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 20px 0 20px 0;
  z-index: 10;
`;

const ThumbnailScrollContainer = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 20px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  justify-content: center;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ThumbnailItem = styled.button<{isActive: boolean}>`
  flex-shrink: 0;
  width: 80px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid ${(props) => (props.isActive ? '#fff' : 'transparent')};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    transform: scale(1.05);
  }

  ${(props) =>
    props.isActive &&
    `
    box-shadow: 0 0 0 2px #fff;
  `}
`;

const ThumbnailImage = styled(Image)`
  object-fit: cover;
  width: 50vw;
  height: 70vh;
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const ProgressBarContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.3);
  z-index: 20;
`;

const ProgressBar = styled.div`
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  width: 0%;
  transition: width 0.1s linear;
`;

const ProductImageCarousel: React.FC<
  React.PropsWithChildren<ProductImageCarouselProps>
> = ({
  mediaItems,
  selectedProduct,
  className,
  enableScrollAdvancement = true,
}) => {
  const thumbnailRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const autoAdvanceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const progressAnimationRef = useRef<number | null>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Use scroll-based carousel advancement
  const {currentIndex, setCurrentIndex, carouselRef} = useScrollCarousel({
    totalItems: mediaItems.length,
    enabled: enableScrollAdvancement && mediaItems.length > 1,
  });

  // Reset to first image when product changes
  const prevSelectedProductRef = useRef(selectedProduct);

  useEffect(() => {
    if (prevSelectedProductRef.current !== selectedProduct) {
      prevSelectedProductRef.current = selectedProduct;
      setCurrentIndex(0);
    }
  }, [selectedProduct, setCurrentIndex]);

  // Auto-advance functions
  const stopAutoAdvance = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    if (progressAnimationRef.current) {
      cancelAnimationFrame(progressAnimationRef.current);
      progressAnimationRef.current = null;
    }
    // Reset progress bar
    if (progressBarRef.current) {
      progressBarRef.current.style.width = '0%';
    }
  }, []);

  const goToSlide = useCallback(
    (index: number) => {
      stopAutoAdvance();
      setCurrentIndex(index);
    },
    [setCurrentIndex, stopAutoAdvance]
  );

  const goToPrevious = useCallback(() => {
    const newIndex =
      currentIndex === 0 ? mediaItems.length - 1 : currentIndex - 1;
    stopAutoAdvance();
    setCurrentIndex(newIndex);
  }, [currentIndex, mediaItems.length, setCurrentIndex, stopAutoAdvance]);

  const goToNext = useCallback(() => {
    const newIndex =
      currentIndex === mediaItems.length - 1 ? 0 : currentIndex + 1;
    stopAutoAdvance();
    setCurrentIndex(newIndex);
  }, [currentIndex, mediaItems.length, setCurrentIndex, stopAutoAdvance]);

  const startAutoAdvance = useCallback(() => {
    if (mediaItems.length <= 1 || isHovered) return;

    // Clear any existing timers
    stopAutoAdvance();

    // Start progress bar animation using requestAnimationFrame
    const duration = 5000; // 5 seconds
    const startTime = Date.now();

    const animateProgress = () => {
      const elapsed = Date.now() - startTime;
      const progressPercent = Math.min((elapsed / duration) * 100, 100);

      if (progressBarRef.current) {
        progressBarRef.current.style.width = `${progressPercent}%`;
      }

      if (progressPercent < 100) {
        progressAnimationRef.current = requestAnimationFrame(animateProgress);
      }
    };

    // Start the animation
    animateProgress();

    // Set timer to advance to next slide
    autoAdvanceTimerRef.current = setTimeout(() => {
      goToNext();
    }, duration);
  }, [mediaItems.length, isHovered, goToNext, stopAutoAdvance]);

  // Start auto-advance when component mounts or conditions change
  useEffect(() => {
    if (mediaItems.length > 1 && !isHovered) {
      startAutoAdvance();
    } else {
      stopAutoAdvance();
    }

    return () => {
      stopAutoAdvance();
    };
  }, [mediaItems.length, isHovered, startAutoAdvance, stopAutoAdvance]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext]);

  if (!mediaItems || mediaItems.length === 0) {
    return (
      <CarouselContainer className={className}>
        <MainMediaContainer>
          <div style={{color: '#666', fontSize: '18px'}}>
            No media available
          </div>
        </MainMediaContainer>
      </CarouselContainer>
    );
  }

  const currentMedia = mediaItems[currentIndex];

  return (
    <>
      <CarouselContainer
        ref={carouselRef}
        className={className}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <MainMediaContainer>
          <MediaWrapper isVideo={currentMedia.type === 'video'}>
            {currentMedia.type === 'video' ? (
              <video
                src={currentMedia.src}
                autoPlay
                loop
                muted
                playsInline
                poster={currentMedia.thumbnail}
              >
                <track kind="captions" src="" srcLang="en" label="English" />
              </video>
            ) : (
              <StyledImage
                src={currentMedia.src}
                alt={currentMedia.alt}
                layout="fill"
                priority={currentIndex === 0}
              />
            )}
          </MediaWrapper>
        </MainMediaContainer>

        {mediaItems.length > 1 && (
          <>
            <NavigationButton
              position="left"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              disabled={mediaItems.length <= 1}
            >
              <IconChevronLeft size={20} />
            </NavigationButton>

            <NavigationButton
              position="right"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              disabled={mediaItems.length <= 1}
            >
              <IconChevronRight size={20} />
            </NavigationButton>
          </>
        )}

        {/* Progress bar for auto-advance */}
        {mediaItems.length > 1 && (
          <ProgressBarContainer>
            <ProgressBar ref={progressBarRef} />
          </ProgressBarContainer>
        )}

        {mediaItems.length > 1 && (
          <ThumbnailContainer>
            <ThumbnailScrollContainer ref={thumbnailRef}>
              {mediaItems.map((item, index) => (
                <ThumbnailItem
                  key={item.id}
                  isActive={index === currentIndex}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToSlide(index);
                  }}
                >
                  <ThumbnailImage
                    src={item.thumbnail || item.src}
                    alt={item.alt}
                    width={80}
                    height={60}
                  />
                  {item.type === 'video' && <PlayButton>▶</PlayButton>}
                </ThumbnailItem>
              ))}
            </ThumbnailScrollContainer>
          </ThumbnailContainer>
        )}
      </CarouselContainer>
    </>
  );
};

export default ProductImageCarousel;
